import type { CampaignOptionsSort, CampaignOptionsStatusFilter } from "@/interfaces/campaing";
import type {
  ImpactDatePreset,
  ImpactEmptySelectionAction,
  ImpactSelectionEmptyMessageInput,
  ImpactViewMode,
} from "@/interfaces/impactReport";
import type { ImpactReportPdfDownloadFailureKind } from "@/interfaces/impactReportPdf";
import type { ImpactReportCardSortMode } from "@/interfaces/impactReportView";
import type {
  CampaignStatus,
  ReportFilterField,
  ReportFilterWireEncoding,
  ReportFilterWireEncodingMap,
} from "@/types/types";

/**
 * THE report-filter wire contract as this app knows it: one row per `ReportFilter` field,
 * declaring how that field is written into the report query string. `utils/impact-report-helper.
 * ts#buildReportFilterQueryString` is the only consumer, and it is the only place this app turns
 * a filter into a URL.
 *
 * Its type (`ReportFilterWireEncodingMap`, see types/types.ts) is EXHAUSTIVE over
 * `keyof ReportFilter`: a field added to the contract fails to compile here until it is declared.
 * Before this map, the same information lived in three hand-kept key lists whose
 * `as const satisfies ReadonlyArray<keyof ReportFilter>` proved every LISTED key was real but
 * never that every REAL key was listed — so a field the user selected and the API accepted could
 * be dropped on the way out with no error anywhere. Each value is also derived from the field's
 * own type, so an encoding cannot contradict the field it describes.
 *
 * COUNTERPART: `apps/gfgf-api/src/constants/impactReport.ts` declares the same map for the API's
 * outbound call to Crunchy AND for its inbound DTO. The two apps deliberately never import from
 * each other; keep the two maps in step by hand, and expect the API's own inbound validation to
 * be the backstop if they ever aren't.
 *
 * ORDER IS LOAD-BEARING — `REPORT_FILTER_WIRE_ENTRIES` iterates in declaration order, which fixes
 * the order of the emitted query string (scalars, then boolean flags, then repeated arrays).
 *
 * NOT PRESENT, and structurally impossible to add: `organizationId`. It is identity, resolved
 * server-side from the authenticated caller — never a filter this app may send.
 *
 * `clientId` IS listed, because it is part of the contract, but nothing in this app sets it and
 * nothing should: the API refuses a `clientId` from a tenant-scoped caller with a 403, because
 * Platform holds no client identifier it could check one against and will not forward an
 * unverifiable tenant selector on a user's behalf. Filter by `campaignIds` instead.
 */
export const REPORT_FILTER_WIRE_ENCODING: ReportFilterWireEncodingMap = {
  clientId: "scalar",
  dateFrom: "scalar",
  dateTo: "scalar",
  timeZone: "scalar",
  minDisplay: "scalar",

  fallbackLanguage: "boolean",
  includeRefunded: "boolean",

  campaignIds: "repeated",
  orderIds: "repeated",
  cause: "repeated",
  metric: "repeated",
  vendor: "repeated",
  sdgIds: "repeated",
};

/**
 * The map as ordered `[field, encoding]` pairs — the form the serializer loops over.
 * `Object.entries` erases the key type to `string`; the assertion restores what the map's own
 * type already proves, namely that its key set is exactly `keyof ReportFilter`.
 */
export const REPORT_FILTER_WIRE_ENTRIES: ReadonlyArray<
  readonly [ReportFilterField, ReportFilterWireEncoding]
> = Object.entries(REPORT_FILTER_WIRE_ENCODING) as Array<
  [ReportFilterField, ReportFilterWireEncoding]
>;

/**
 * ── Hero view ───────────────────────────────────────────────────────────────────────────────
 *
 * "Platform formats, never translates": everything below is either a display CONSTRAINT (the
 * stat cap) or Platform's OWN brand voice (the masthead label, the prepared-by line, the "All
 * time" fallback) — never a rendering of language Crunchy sent. Copy describing the client's
 * giving always comes off the wire; nothing here does.
 */

/**
 * The hero prints the FIRST six `impactTypes` entries per cause, array order as received — no
 * spill/"(continued)" page, no re-sort. See `utils/impact-report-helper.ts`'s page-model builder.
 */
export const IMPACT_REPORT_HERO_STAT_CAP = 6;

/**
 * The single bundled fallback backdrop — an SVG stand-in, not a photograph, until a designer
 * supplies real imagery. Used whenever `CauseSection.image` is `null` (true for every cause until
 * per-cause hero imagery ships upstream) and as the `onerror` fallback for a URL that fails to
 * load. Also the cover/closing background: the contract has no field for either.
 *
 * Served in dev by `apps/gfgf-ui/index.ts`'s `/images/*` route and in production by
 * `apps/gfgf-api/src/index.ts`'s `serveStatic` over `apps/gfgf-ui/public` — the one asset
 * location in the repo that resolves in both without a server change.
 */
export const IMPACT_REPORT_FALLBACK_IMAGE_SRC = "/images/impact-report-fallback.svg";

/**
 * A same-origin copy of `GFG_LOGO` (`constants/campaignBuilder.ts`), read ONLY by the PDF
 * serialiser's data-URI resolution (`resolveImpactReportBrandAssets`). The on-screen masthead keeps
 * rendering `GFG_LOGO` itself.
 *
 * The R2 bucket `GFG_LOGO` lives on sends no `access-control-allow-origin` header and 403s the
 * CORS preflight. An `<img src>` is not subject to CORS, so the screen has always rendered it, but
 * the serialiser's `fetch()` of the same URL can never succeed from a browser — and one failed
 * asset fails every download. Served from `public/images/` like the fallback backdrop above, so
 * the read is same-origin. If the brand mark is redesigned, re-export this file from the new art.
 */
export const IMPACT_REPORT_MASTHEAD_LOCKUP_SRC = "/images/impact-report-masthead-lockup.png";

/** Masthead label — Platform's own brand voice, not the report's language. */
export const IMPACT_REPORT_MASTHEAD_LABEL = "IMPACT GIFT REPORT";

/** Masthead attribution line. */
export const IMPACT_REPORT_PREPARED_BY_LABEL = "Prepared by Gifts for Good";

/** Alt text for the masthead lockup — names the mark itself, not the prepared-by sentence. */
export const IMPACT_REPORT_MASTHEAD_LOGO_ALT = "Gifts for Good";

/**
 * `data.dateRange` is the MATCHED bounds of what the report covers, and both ends are `null`
 * together on an empty result set (never an echo of the requested window). This is what the
 * masthead reads in that case — matching the prototype's own fallback.
 */
export const IMPACT_REPORT_ALL_TIME_LABEL = "All time";

/**
 * ── Closing page + colophon footer ─────────────────────────────────────────────────────────
 *
 * Same rule as above: this is Platform's own brand voice, not a rendering of anything Crunchy
 * sent. `data.thankYou.message` (the closing headline) is the one piece of this page that IS
 * wire-sourced when present — see `utils/impact-report-helper.ts`'s page-model builder for its
 * temporary fallback.
 */

/**
 * Master switch for every PDF-export affordance in the report — the closing page's button and the
 * control bar's download control. ON: the export path exists end to end — Platform's routes over
 * the queued converter (`api/impactReportPdf.ts`), the serialiser
 * (`utils/impact-report-document-helper.ts`) and the one download function both affordances call
 * (`utils/impact-report-pdf-download-helper.ts`). Setting it back to `false` removes both
 * affordances from the DOM without touching either component.
 */
export const IMPACT_REPORT_PDF_EXPORT_ENABLED = true;

/** Download affordance label — rendered only while `IMPACT_REPORT_PDF_EXPORT_ENABLED` is on. */
export const IMPACT_REPORT_DOWNLOAD_BUTTON_LABEL = "Download Report (PDF)";

/** The control bar's icon-only download control's accessible name and hover title. */
export const IMPACT_REPORT_PDF_DOWNLOAD_ARIA_LABEL = "Download report as PDF";

/**
 * Shown on the closing page's button, and announced from both affordances, for the WHOLE queued
 * render: the job can wait behind others at the converter's concurrency of one before it starts,
 * and a control that goes quiet for that long reads as dead.
 */
export const IMPACT_REPORT_PDF_DOWNLOAD_PENDING_LABEL = "Preparing your PDF…";

/** How long a failed download's toast stays up. Long enough to read a sentence of copy. */
export const IMPACT_REPORT_PDF_DOWNLOAD_ERROR_TOAST_MS = 8_000;

/**
 * ── The PDF download's transport, timing and copy ──────────────────────────────────────────
 *
 * Read by `api/impactReportPdf.ts` (the path and the content type),
 * `utils/impact-report-pdf-download-helper.ts` (the timing) and
 * `components/impactReport/impact-report-error.ts` (the copy).
 */

/** Platform's PDF conversion resource. `/:jobId` is a job's status, `/:jobId/content` its bytes. */
export const IMPACT_REPORT_PDF_CONVERSIONS_PATH = "/reports/client-impact-report/pdf-conversions";

/** The only media type the collect route's body may carry to be saved as the report. */
export const IMPACT_REPORT_PDF_CONTENT_TYPE = "application/pdf";

/**
 * A PDF's header and end-of-file markers, and how far from each end a reader looks for them. Platform
 * sets `application/pdf` on whatever the converter streams, so the header alone proves nothing:
 * a body without `%PDF-` is not a PDF, and one without `%%EOF` was cut off.
 */
export const IMPACT_REPORT_PDF_HEADER_MARKER = "%PDF-";
export const IMPACT_REPORT_PDF_EOF_MARKER = "%%EOF";
export const IMPACT_REPORT_PDF_MARKER_WINDOW_BYTES = 1024;

/** How often the browser asks for a queued or processing job's status. */
export const IMPACT_REPORT_PDF_POLL_INTERVAL_MS = 2_000;

/**
 * The whole download's ceiling, from the click to the saved file. Generous on purpose: a render
 * can sit behind other jobs at the converter's concurrency of one before it even starts. It bounds
 * the asset fetches too, which run before any job exists and have no timeout of their own.
 */
export const IMPACT_REPORT_PDF_DOWNLOAD_DEADLINE_MS = 10 * 60 * 1000;

/**
 * Status polls that may fail in a row (a 5xx, a 429, a dropped connection) before the download
 * gives up on the job. One lost poll must not discard a render the converter is about to finish.
 */
export const IMPACT_REPORT_PDF_POLL_MAX_CONSECUTIVE_FAILURES = 5;

/**
 * The organization-name filename suggestion is clamped to this BEFORE it is sent, under the API
 * DTO's own 100-character bound — so a long name is a duller filename, never a 400.
 */
export const IMPACT_REPORT_PDF_SUGGESTED_FILENAME_MAX_LENGTH = 80;

/** The saved file's stem when the report carries no organization name. */
export const IMPACT_REPORT_PDF_FALLBACK_FILENAME_STEM = "impact-report";

/**
 * What the user is told when a download fails, by cause. A `converter` failure that carries
 * Platform's own reason shows that reason instead
 * (`components/impactReport/impact-report-error.ts#resolveImpactReportPdfErrorMessage`).
 */
export const IMPACT_REPORT_PDF_DOWNLOAD_ERROR_MESSAGE = {
  converter: "We couldn't prepare your PDF. Try again.",
  config: "PDF downloads aren't available right now. Try again later, or contact support.",
  expired: "This PDF request expired before it was ready. Start the download again.",
  serialisation:
    "We couldn't gather this report's images and styles for the PDF. Refresh the page and try again.",
  timeout: "This PDF is taking longer than expected. Try again in a few minutes.",
} as const satisfies Record<ImpactReportPdfDownloadFailureKind, string>;

/**
 * The white Gifts for Good mark at the foot of the closing page — the same asset the design of
 * record uses for this element.
 *
 * This previously pointed at a local `/svg/` path that was never shipped, so the mark 404'd and
 * rendered as a broken image. Its comment also claimed to reuse Drawer.tsx's mark; Drawer actually
 * renders `GFG_LOGO`, a 280x140 R2 PNG — too small for this element, which displays at 120px tall
 * and would be visibly soft on a retina display. This asset is 600x400, so it has real headroom.
 *
 * `.irv-closing-logo`'s `brightness(0) invert(1)` filter is kept even though this source is
 * already white: the filter flattens ANY source to pure white while preserving alpha, so it costs
 * nothing here and keeps the element correct if the asset is ever swapped for a coloured mark.
 */
export const IMPACT_REPORT_GFG_MARK_SRC =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/White_gfg_logo_2026.png?v=1784088994";

/** Colophon column heading — omitted entirely, heading included, when there are no credits. */
export const IMPACT_REPORT_PHOTO_CREDITS_LABEL = "Photo Credits";

/** Colophon column heading for the About block. */
export const IMPACT_REPORT_ABOUT_LABEL = "About Gifts for Good";

/** About Gifts for Good boilerplate — Platform's own brand copy, never the report's language. */
export const IMPACT_REPORT_ABOUT_TEXT =
  "Gifts for Good is the corporate gift platform built so impact is the default, not the exception. 100% of our catalog is sourced from nonprofit and social-enterprise partners, and every gift carries a verified impact metric.";

/**
 * Decorative certification badges at the end of the colophon. Referenced from the prototype's
 * own CDN rather than bundled — they are decorative and can be dropped or bundled locally later
 * without affecting anything else about the colophon.
 */
export const IMPACT_REPORT_CERTIFICATION_BADGES: ReadonlyArray<{
  readonly src: string;
  readonly alt: string;
}> = [
  {
    src: "https://cdn.shopify.com/s/files/1/1826/6559/files/B_Corp.png?v=1675378899",
    alt: "Certified B Corporation",
  },
  {
    src: "https://cdn.shopify.com/s/files/1/1826/6559/files/women_owned.svg?v=1722834156",
    alt: "Women Owned",
  },
];

/**
 * ── Card view ───────────────────────────────────────────────────────────────────────────────
 *
 * Same rule as the hero section above: every string here is PLATFORM'S OWN UI CHROME — a
 * heading, a control's labels, an empty state. None of it describes the client's giving, which
 * always arrives already worded on the wire. The card view's own impact language is
 * `statements.narrative.sentence`, rendered verbatim and composed nowhere.
 *
 * The bundled fallback image is NOT re-declared here: the card reuses
 * `IMPACT_REPORT_FALLBACK_IMAGE_SRC` above. One last-resort asset exists for the whole report —
 * a second one is the start of the source ladder that was deliberately removed upstream.
 */

/** Section heading above the grid. */
export const IMPACT_REPORT_CARD_HEADING = "Explore your Impact";

/** The sort control's own label. */
export const IMPACT_REPORT_CARD_SORT_LABEL = "Sort by";

/**
 * The order that is selected on first render. The client asked for the largest figures to lead,
 * and the units genuinely are incommensurable — 280 children against 35,000 minutes — so this is
 * a raw-magnitude ordering, never a ranking of significance. That is exactly why no normalisation
 * is invented: there is no common unit to normalise to, and inventing one would have Platform
 * assert an editorial verdict about a client's giving. That is the same reasoning
 * `IMPACT_REPORT_CARD_SORT_MODE_LABELS` below spells out for its "most", never "biggest impact"
 * label. Rows with no narrative register have no quantity to rank by
 * and sort last, alphabetically among themselves — a missing quantity is not a zero.
 */
export const IMPACT_REPORT_CARD_DEFAULT_SORT_MODE: ImpactReportCardSortMode = "quantity";

/**
 * One label per sort mode. EXHAUSTIVE over `ImpactReportCardSortMode` by its own type, so adding
 * an order to the union fails to compile until the control has a word for it — the same
 * derive-don't-restate discipline `REPORT_FILTER_WIRE_ENCODING` uses above.
 *
 * The quantity label says "most", never "biggest impact": the ranking compares minutes against
 * trees, so it answers "what did we do the most of" and must not read as an editorial verdict.
 */
export const IMPACT_REPORT_CARD_SORT_MODE_LABELS: Readonly<
  Record<ImpactReportCardSortMode, string>
> = {
  alphabetical: "Impact type (A–Z)",
  quantity: "Quantity (most first)",
};

/**
 * The labels as ordered `[mode, label]` pairs — the form the sort control loops over, so the
 * option order is fixed here rather than left to a component's own list. `Object.entries` erases
 * the key type to `string`; the assertion restores what the record's type already proves.
 */
export const IMPACT_REPORT_CARD_SORT_MODE_ENTRIES: ReadonlyArray<
  readonly [ImpactReportCardSortMode, string]
> = Object.entries(IMPACT_REPORT_CARD_SORT_MODE_LABELS) as Array<
  [ImpactReportCardSortMode, string]
>;

/**
 * Shown instead of a bare grid when the payload carries no impact types at all. It says "nothing
 * yet", not "nothing matched": until the filter controls exist there is nothing a reader could
 * have narrowed, and blaming an absent filter for an empty account would be a guess.
 */
export const IMPACT_REPORT_CARD_EMPTY_STATE_TEXT =
  "No impact to show yet. As gifts are delivered, every kind of impact they create will appear here.";

/**
 * The last-resort body copy for the card view's error state, used whenever the failed response
 * carried no API message of its own. Deliberately short and generic: the console — via
 * `utils/impact-report-helper.ts#describeImpactReportError`, the one place the raw error is
 * redacted — is where the discriminating detail (`DashboardReportErrorCode`, HTTP status) is
 * meant to survive, never the screen.
 */
export const IMPACT_REPORT_CARD_ERROR_STATE_TEXT =
  "We couldn't load your impact data. Please try refreshing the page.";

/**
 * ── Control bar ─────────────────────────────────────────────────────────────────────────────
 *
 * The four controls that decide what the two renderers draw, plus the one route literal the app
 * bar and the page share. Same rule as every section above: these strings are PLATFORM'S OWN UI
 * chrome. Nothing here describes the client's giving, and nothing here is ever compared against a
 * value on the wire — the values that ARE compared upstream (cause values, campaign ids) travel
 * verbatim from the payload and never through a constant.
 */

/**
 * The Impact route's absolute path, as registered in `App.tsx` (`path="impact"` under the
 * authenticated layout). Declared once so `Drawer.tsx`'s route match and the page's own links
 * cannot drift apart — an app bar matching a path the router no longer serves fails silently, by
 * simply never swapping.
 */
export const IMPACT_REPORT_ROUTE_PATH = "/impact";

/**
 * The emailed report link's route, under the Impact route. The email's link is built by the report
 * worker from its `PLATFORM_REPORT_URL`, and `App.tsx` declares this same path literally (as it
 * does `/impact`), so a change here must be made in both places and in that env var.
 * `buildImpactReportJobPath` is for any in-app link to it.
 */
export const IMPACT_REPORT_JOB_ROUTE = "/impact/reports/:jobId";

export function buildImpactReportJobPath(jobId: string): string {
  return IMPACT_REPORT_JOB_ROUTE.replace(":jobId", encodeURIComponent(jobId));
}

/**
 * How often a deferred report's job is polled. Only while the tab is visible — react-query pauses
 * interval refetches in a hidden tab.
 */
export const IMPACT_REPORT_JOB_POLL_INTERVAL_MS = 30_000;

/**
 * ── The deferral sessionStorage cache ───────────────────────────────────────────────────────
 *
 * A DEFERRED answer (202, `{status:"deferred",jobId,recipient}`) is held `staleTime: Infinity` in
 * react-query (see `hooks/useImpactReport.ts`), but react-query still garbage-collects the query
 * `gcTime` (default 5 minutes) after its last observer unmounts. The selection lives in
 * `ImpactReportProvider`, which survives navigation, so returning to `/impact` after the tab has
 * spent 5+ minutes elsewhere — or reloading the page — would otherwise re-issue the GET with no
 * cache entry to answer from. EVERY re-issued request starts a brand-new upstream job and sends
 * ANOTHER email — it is never a cheap re-read. `utils/impact-report-helper.ts` persists
 * the deferral in `sessionStorage`, keyed by USER and filter, so the SAME deferral answers the
 * query again with no network call, for as long as the browser tab's session lasts.
 */

/**
 * Namespaces every sessionStorage key this app writes for a deferred impact-report answer. The
 * signed-in user's id and a per-filter suffix (the react-query hash of that filter's query key —
 * see `utils/impact-report-helper.ts#impactReportQueryKey`) follow this prefix, so distinct
 * selections never collide, a stray sessionStorage key from another feature is never mistaken for
 * one of these, AND a different user signing in on the same tab can never read a previous user's
 * stored deferral (their job id, their `recipient` email) for a selection that happens to match.
 * `utils/impact-report-helper.ts`'s three per-entry storage functions all take the current user's
 * id as their first argument and read/write nothing when it is `null`; the fourth,
 * `clearStoredImpactReportDeferrals`, removes every key under this prefix on sign-out/user switch.
 */
export const IMPACT_REPORT_DEFERRAL_STORAGE_PREFIX = "gfgf:impactReportDeferral:";

/**
 * How long a stored deferral is trusted before a read discards it (and removes the entry) rather
 * than returning it. Matches the job's own lifetime — `IMPACT_REPORT_JOB_NOT_FOUND_BODY` tells the
 * reader "links last 7 days" — so a cached deferral never outlives the job it stands in for.
 */
export const IMPACT_REPORT_DEFERRAL_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** The four control buttons' own words — one uppercase word each, as in the prototype's bar. */
export const IMPACT_REPORT_CONTROL_LABELS = {
  view: "VIEW",
  cause: "CAUSE",
  campaign: "CAMPAIGN",
  date: "DATE",
} as const;

/**
 * What the CAMPAIGN control reads when nothing is selected and the report covers the caller's
 * whole organization. The default, not an active filter, so the control stays unlit.
 */
export const IMPACT_REPORT_ALL_CAMPAIGNS_LABEL = "All campaigns";

/**
 * What the VIEW control calls each renderer. EXHAUSTIVE over `ImpactViewMode` by its own type, so
 * adding a renderer fails to compile until the control has a word for it.
 *
 * The wording lives here and the state values stay `hero` / `card`, so re-labelling the control
 * never touches the mode value that the page, the context and this file all branch on.
 */
export const IMPACT_REPORT_VIEW_MODE_LABELS = {
  hero: "Impact Report",
  card: "Card Report",
} as const satisfies Readonly<Record<ImpactViewMode, string>>;

/**
 * The labels as ordered `[mode, label]` pairs — the form the VIEW control loops over, so option
 * order is fixed here rather than left to a component's own list. `Object.entries` erases the key
 * type to `string`; the assertion restores what the record's type already proves.
 */
export const IMPACT_REPORT_VIEW_MODE_ENTRIES: ReadonlyArray<readonly [ImpactViewMode, string]> =
  Object.entries(IMPACT_REPORT_VIEW_MODE_LABELS) as Array<[ImpactViewMode, string]>;

/** The renderer selected on first render — the full-bleed document the report is named for. */
export const IMPACT_REPORT_DEFAULT_VIEW_MODE: ImpactViewMode = "hero";

/**
 * One label per date preset, mirroring the prototype's `irDateRange` set. EXHAUSTIVE over
 * `ImpactDatePreset`, so adding a preset fails to compile until the menu has a word for it.
 *
 * `allTime` deliberately does NOT reuse `IMPACT_REPORT_ALL_TIME_LABEL` above despite the identical
 * string today: that one labels `data.dateRange` when the MATCHED bounds are null (a fact about
 * the data that came back), this one labels a REQUEST the user can select. They answer different
 * questions and are free to diverge in wording without dragging the other with them.
 */
export const IMPACT_REPORT_DATE_PRESET_LABELS = {
  allTime: "All time",
  last30Days: "Last 30 days",
  last90Days: "Last 90 days",
  thisYear: "This year",
  lastYear: "Last year",
  custom: "Custom range…",
} as const satisfies Readonly<Record<ImpactDatePreset, string>>;

/**
 * The SENTENCE form of each preset that requests a window, for the empty-state message ("…
 * in the last 30 days"). `allTime` requests no window and `custom` is phrased from its own
 * bounds, so neither has an entry; every other preset must, or this fails to compile.
 */
export const IMPACT_REPORT_DATE_PRESET_PHRASES = {
  last30Days: "in the last 30 days",
  last90Days: "in the last 90 days",
  thisYear: "this year",
  lastYear: "last year",
} as const satisfies Readonly<Record<Exclude<ImpactDatePreset, "allTime" | "custom">, string>>;

/**
 * The presets as ordered `[preset, label]` pairs — the DATE menu's option order, fixed here.
 * Widest first, then narrowing, with the custom range last because it opens a sub-form.
 */
export const IMPACT_REPORT_DATE_PRESET_ENTRIES: ReadonlyArray<readonly [ImpactDatePreset, string]> =
  Object.entries(IMPACT_REPORT_DATE_PRESET_LABELS) as Array<[ImpactDatePreset, string]>;

/**
 * The preset a cleared control returns to. `allTime` emits no date fields at all, which is the
 * only date state that asserts nothing about when the account's giving began.
 */
export const IMPACT_REPORT_DEFAULT_DATE_PRESET: ImpactDatePreset = "allTime";

/** How many days back the two rolling presets reach, mirroring the prototype's `irDateRange`. */
export const IMPACT_REPORT_LAST_30_DAYS_SPAN = 30;
export const IMPACT_REPORT_LAST_90_DAYS_SPAN = 90;

/**
 * Ceiling on how many ids one report filter may carry per id field — the UI's own copy of the
 * API's limit.
 *
 * TRANSCRIBED, NEVER IMPORTED: `apps/gfgf-api/src/constants/impactReport.ts` declares the same
 * `IMPACT_REPORT_MAX_FILTER_IDS = 250`, enforced there as a zod `.max()` and again in the
 * ownership service. The two apps deliberately never import from each other (the same discipline
 * `REPORT_FILTER_WIRE_ENCODING` above is kept under); keep the two values in step by hand, and
 * expect the API's 400 to be the backstop if they ever aren't.
 *
 * Selection-first page load takes this off the normal path entirely — nothing is enumerated on
 * mount. It bites only where a user in an organization with more than 250 campaigns selects all
 * of them, and `utils/impact-report-filter-helper.ts#capImpactCampaignSelection` reports the
 * capping so the UI can NAME it. Never truncate silently: a report that quietly answers "your
 * first 250 campaigns" while presenting itself as complete impact is a wrong answer, not a
 * limitation.
 */
export const IMPACT_REPORT_MAX_FILTER_IDS = 250;

/** The secondary text on a campaign picker row — how many of its gifts have been redeemed. */
export function buildImpactReportCampaignRedeemedLabel(count: number): string {
  return `${count.toLocaleString()} redeemed`;
}

/** The control bar's own landmark label, for anyone navigating the app bar by region. */
export const IMPACT_REPORT_CONTROL_BAR_ARIA_LABEL = "Impact report controls";

/**
 * Marks every `ImpactFilterMenu` trigger button on the bar — the default icon+word button AND any
 * `renderTrigger` custom one (the collapsed filters pill). One control's backdrop-click handler
 * reads this off whatever DOM node sits under the click to recognise "this is ANOTHER control's
 * trigger, forward the click" rather than "this is empty page, just dismiss" — see
 * `ImpactFilterMenu`'s backdrop handling for why that distinction is the whole fix for the
 * one-tap-only-closes bug.
 */
export const IMPACT_FILTER_TRIGGER_ATTRIBUTE = "data-impact-filter-trigger";

/**
 * The CAUSE menu before any report has been fetched. The options are read off a payload's
 * `causeData`, so until a campaign is chosen there is genuinely nothing to list — and saying "no
 * causes" there would assert something about the account that has not been asked yet.
 */
export const IMPACT_REPORT_CAUSE_MENU_EMPTY_TEXT =
  "Choose a campaign first — the causes it touched will appear here.";

// ── Campaign picker dialog ─────────────────────────────────────────────────────────────────────

/** Rows per `GET /campaigns/options` page in the picker's infinite list. */
export const IMPACT_CAMPAIGN_PICKER_PAGE_SIZE = 50;

/** Quiet time after the last keystroke before the picker's search hits the server. */
export const IMPACT_CAMPAIGN_PICKER_SEARCH_DEBOUNCE_MS = 250;

/**
 * Longest search the picker sends. TRANSCRIBED, NEVER IMPORTED: the API's
 * `CAMPAIGN_OPTIONS_SEARCH_MAX_LENGTH` rejects a longer one with a 400, which the picker would show
 * as a load failure. Keep the two in step by hand.
 */
export const IMPACT_CAMPAIGN_PICKER_SEARCH_MAX_LENGTH = 100;

/** How many recently applied campaigns the picker's Recent section remembers per user. */
export const IMPACT_CAMPAIGN_RECENTS_MAX = 5;

/** localStorage key prefix for the picker's recents; the user id is appended. */
export const IMPACT_CAMPAIGN_RECENTS_STORAGE_KEY_PREFIX = "gfg.impactReport.recentCampaigns.";

/** localStorage key prefix for whether the Recent section is collapsed; the user id is appended. */
export const IMPACT_CAMPAIGN_RECENTS_COLLAPSED_STORAGE_KEY_PREFIX =
  "gfg.impactReport.recentCampaignsCollapsed.";

/**
 * localStorage key prefix for the picker's "Hide campaigns with no redemptions" toggle; the user
 * id is appended.
 */
export const IMPACT_CAMPAIGN_HIDE_UNREDEEMED_STORAGE_KEY_PREFIX =
  "gfg.impactReport.hideUnredeemedCampaigns.";

/** The picker's sort select, in display order. */
export const IMPACT_CAMPAIGN_PICKER_SORT_OPTIONS = [
  { value: "active", label: "Active first" },
  { value: "redeemed", label: "Most redeemed" },
  { value: "newest", label: "Newest" },
  { value: "name", label: "A–Z" },
] as const satisfies ReadonlyArray<{ value: CampaignOptionsSort; label: string }>;

/** The picker's status chips, in display order. */
export const IMPACT_CAMPAIGN_PICKER_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
] as const satisfies ReadonlyArray<{ value: CampaignOptionsStatusFilter; label: string }>;

export const IMPACT_CAMPAIGN_PICKER_TITLE = "Choose campaigns";
export const IMPACT_CAMPAIGN_PICKER_CLOSE_LABEL = "Close";
export const IMPACT_CAMPAIGN_PICKER_SEARCH_PLACEHOLDER = "Search campaigns";
/** The ✕ inside the search box, shown while it holds text. */
export const IMPACT_CAMPAIGN_PICKER_CLEAR_SEARCH_LABEL = "Clear search";
export const IMPACT_CAMPAIGN_PICKER_SORT_LABEL = "Sort";
export const IMPACT_CAMPAIGN_PICKER_STATUS_FILTER_LABEL = "Show";
export const IMPACT_CAMPAIGN_PICKER_RECENT_HEADING = "Recent";

/** Heading over the full list; `total` is the filtered count the server reports. */
export function buildImpactCampaignPickerListHeading(total: number): string {
  return `All campaigns (${total.toLocaleString()})`;
}

export const IMPACT_CAMPAIGN_PICKER_LOADING_TEXT = "Loading campaigns…";
export const IMPACT_CAMPAIGN_PICKER_EMPTY_TEXT = "No campaigns match your search.";
export const IMPACT_CAMPAIGN_PICKER_ERROR_TEXT = "We couldn't load your campaigns.";
export const IMPACT_CAMPAIGN_PICKER_RETRY_LABEL = "Try again";

/** The switch under Sort/Show; on by default, remembered per user. */
export const IMPACT_CAMPAIGN_PICKER_HIDE_UNREDEEMED_LABEL = "Hide campaigns with no redemptions";

/**
 * Under the list heading while the switch hides anything, so the hiding is never silent. `count`
 * is the page's `hiddenCount`.
 */
export function buildImpactCampaignPickerHiddenLabel(count: number): string {
  const noun = count === 1 ? "campaign" : "campaigns";
  return `${count.toLocaleString()} ${noun} with no redemptions hidden`;
}

/** Replaces the empty text when nothing is left to show but the switch hid `count` campaigns. */
export function buildImpactCampaignPickerOnlyHiddenLabel(count: number): string {
  const verb = count === 1 ? "is" : "are";
  return `No campaigns with redemptions match. ${count.toLocaleString()} with no redemptions ${verb} hidden.`;
}

/** Turns the switch off from the hidden hint or the empty state; "Show" is the status label. */
export const IMPACT_CAMPAIGN_PICKER_SHOW_HIDDEN_LABEL = "Show them";

/** Select-all row; offered only when the filtered total fits under the filter-id cap. */
export function buildImpactCampaignPickerSelectAllLabel(total: number): string {
  return `Select all ${total.toLocaleString()}`;
}

/** Replaces the select-all row when the filtered total exceeds the filter-id cap. */
export const IMPACT_CAMPAIGN_PICKER_TOO_MANY_HINT = `Too many to report on at once. Narrow with search or status to ${IMPACT_REPORT_MAX_FILTER_IDS} or fewer.`;

/** Shown once the selection reaches the filter-id cap. */
export const IMPACT_CAMPAIGN_PICKER_CAP_HINT = `You can report on up to ${IMPACT_REPORT_MAX_FILTER_IDS} campaigns at a time.`;

/** Shown when the server's scan ceiling cut the list (`truncated`). */
export const IMPACT_CAMPAIGN_PICKER_TRUNCATED_HINT =
  "Showing your newest 5,000 campaigns. Search to find older ones.";

export const IMPACT_CAMPAIGN_PICKER_LOAD_MORE_LABEL = "Load more";

export function buildImpactCampaignPickerSelectedLabel(count: number): string {
  return `${count.toLocaleString()} selected`;
}

export const IMPACT_CAMPAIGN_PICKER_CLEAR_LABEL = "Clear";
export const IMPACT_CAMPAIGN_PICKER_CANCEL_LABEL = "Cancel";
export const IMPACT_CAMPAIGN_PICKER_APPLY_LABEL = "Apply";

/**
 * Row status labels; `closed` reads as "Ended" to match the status chip. Partial because the picker
 * only ever lists campaigns worth reporting on (active/scheduled/closed/draft) — the deliberate-act
 * states (paused/canceled/archived/deleted) never reach it; the use site falls back to the raw value.
 */
export const IMPACT_CAMPAIGN_PICKER_STATUS_LABELS: Partial<Record<CampaignStatus, string>> = {
  active: "Active",
  scheduled: "Scheduled",
  closed: "Ended",
  draft: "Draft",
};

/** Takes the redeemed count's place on a row with 0 redeemed. */
export const IMPACT_CAMPAIGN_PICKER_NO_REDEMPTIONS_TAG = "No redemptions yet";

/** The custom-range sub-form inside the DATE menu. */
export const IMPACT_REPORT_DATE_CUSTOM_FROM_LABEL = "From";
export const IMPACT_REPORT_DATE_CUSTOM_TO_LABEL = "To";
export const IMPACT_REPORT_DATE_CUSTOM_APPLY_LABEL = "Apply range";

/**
 * Shown under the custom-range inputs while only one bound is filled. An incomplete range
 * resolves to NO window (`resolveImpactDateWindow` returns `null`), i.e. all time — say so before
 * the user applies it rather than letting the report silently widen.
 */
export const IMPACT_REPORT_DATE_CUSTOM_INCOMPLETE_TEXT =
  "Fill both dates to apply a range. Applied with one date missing, the report covers all time.";

/**
 * ── The pre-selection prompt ────────────────────────────────────────────────────────────────
 *
 * The page is selection-first: the report is an expensive server-side aggregate and the endpoint
 * requires a scope, so no request is issued until the selection carries one. While the query is
 * disabled the report area shows THIS, and it never says the selection has no impact: nothing has
 * been asked yet, so nothing has come back empty.
 */
export const IMPACT_REPORT_PRE_SELECTION_TITLE = "Choose a campaign to build your report";
/**
 * Names the CAMPAIGN picker, since that is where the selection is made — the picker searches every
 * campaign, so the prompt promises no more than the picker can deliver.
 */
export const IMPACT_REPORT_PRE_SELECTION_BODY =
  "Click CAMPAIGN above to search and choose the campaigns you want to report on. Your impact report is built from that selection.";

/**
 * The line under the pre-selection body saying how many LISTED campaigns have anything to report,
 * so a user knows before choosing whether a pick can come back with results. Counts only what the
 * menu holds — never a claim about the whole account.
 */
export function buildImpactReportPreSelectionCoverage(
  withRedemptions: number,
  listed: number,
): string {
  const listedText = listed.toLocaleString();
  if (withRedemptions === 0) {
    return listed === 1
      ? "The one listed campaign has no redeemed gifts yet."
      : `None of the ${listedText} listed campaigns has redeemed gifts yet.`;
  }
  const verb = withRedemptions === 1 ? "has" : "have";
  const noun = listed === 1 ? "campaign" : "campaigns";
  return `${withRedemptions.toLocaleString()} of ${listedText} listed ${noun} ${verb} redeemed gifts.`;
}

/**
 * ── The other two empty states — an EMPTY payload, two different reasons ────────────────────
 *
 * Checked in this order by `components/impactReport/ImpactEmptyState.tsx#resolveImpactEmptyStateKind`:
 *
 *   1. NO DEDICATED PAGE — `causeData` is empty but the cover still reports causes touched
 *      (`summary.mainCause.numCauses > 0`). The giving landed somewhere; it just has no
 *      renderable cause page. Deliberately does NOT tell the reader to switch views: the card
 *      grid flattens this SAME `causeData`, so switching shows the same nothing, and sending
 *      someone to a view that cannot help them is worse than saying less.
 *   2. SELECTION EMPTY — nothing came back for what was asked. The message names the selection
 *      itself (campaigns, causes, requested window) rather than guessing at the account's history,
 *      and offers exactly one way out (see `IMPACT_REPORT_EMPTY_SELECTION_ACTION_LABELS`).
 */
export const IMPACT_REPORT_NO_DEDICATED_PAGE_TITLE = "No dedicated page for this giving yet";
export const IMPACT_REPORT_NO_DEDICATED_PAGE_BODY =
  "This selection's giving supports causes that don't have a report page of their own yet. It still counts — it just isn't shown here.";

export const IMPACT_REPORT_SELECTION_EMPTY_TITLE = "No impact recorded for this selection";

export const IMPACT_REPORT_NO_CAMPAIGNS_TITLE = "No campaigns in your groups yet";
export const IMPACT_REPORT_NO_CAMPAIGNS_BODY =
  "Once your groups have campaigns, their impact will show here.";

/**
 * “Spring Gifting” has no recorded impact for Clean Water in the last 30 days.
 * The 3 campaigns you picked have no recorded impact for A, B or C.
 *
 * The date phrase is the REQUESTED window (`describeImpactDatePhrase`), never the payload's
 * `dateRange`, which is `null` on exactly the result this sentence describes.
 */
export function buildImpactReportSelectionEmptyMessage({
  campaignCount,
  singleCampaignName,
  causeNames,
  datePhrase,
}: ImpactSelectionEmptyMessageInput): string {
  const subject =
    singleCampaignName !== null
      ? `“${singleCampaignName}”`
      : campaignCount === 1
        ? "The campaign you picked"
        : `The ${campaignCount.toLocaleString()} campaigns you picked`;
  const verb = singleCampaignName !== null || campaignCount === 1 ? "has" : "have";
  const causeClause = causeNames.length > 0 ? ` for ${joinWithOr(causeNames)}` : "";
  const dateClause = datePhrase !== null ? ` ${datePhrase}` : "";
  return `${subject} ${verb} no recorded impact${causeClause}${dateClause}.`;
}

/** "A", "A or B", "A, B or C" — no serial comma. */
function joinWithOr(items: readonly string[]): string {
  const leading = items.slice(0, -1);
  const last = items.slice(-1).join("");
  return leading.length > 0 ? `${leading.join(", ")} or ${last}` : last;
}

/**
 * The `selectionEmpty` state's one button, per `resolveImpactEmptySelectionAction`'s choice.
 * Exhaustive over the action type, so a new action fails to compile until it is labelled here.
 */
export const IMPACT_REPORT_EMPTY_SELECTION_ACTION_LABELS = {
  widenDate: "Widen the date range",
  clearCause: "Clear the cause filter",
  showCampaigns: "Show campaigns with redeemed gifts",
} as const satisfies Readonly<Record<ImpactEmptySelectionAction, string>>;

/**
 * ── The 250-id cap, said out loud ───────────────────────────────────────────────────────────
 *
 * `capImpactCampaignSelection` bounds the campaign list because the API rejects a longer one with
 * a 400, and it reports the bounding back rather than performing it in silence. This is the copy
 * that makes it legible: a report that quietly answers "your first 250 campaigns" while
 * presenting itself as the client's complete impact is a WRONG ANSWER, not a limitation.
 *
 * Rare by construction — selection-first means nothing is enumerated on mount, so it takes an
 * organization with more than 250 campaigns whose user selects them all. Rare is not silent.
 */
export const IMPACT_REPORT_CAPPED_SELECTION_TITLE = "This report covers part of your selection";

/**
 * Names BOTH numbers and the limit, because "some campaigns were dropped" is not actionable and
 * "250 of 312" is. A function rather than a template string so the numbers cannot be interpolated
 * into it at a call site with different wording each time.
 */
export function buildImpactReportCappedSelectionNotice(
  selectedCount: number,
  limit: number,
): string {
  return `You selected ${selectedCount} campaigns and one report can cover at most ${limit}, so this report covers ${limit} of them. Narrow the selection to report on the rest.`;
}

/**
 * ── Active-filter pills ─────────────────────────────────────────────────────────────────────
 *
 * The pills on the bar's LEFT are a second rendering of the state the four controls on the right
 * already hold — they add no state of their own. Every label here is Platform's own UI chrome,
 * same rule as the rest of this file; the values a pill NAMES (a `causeName`, a campaign name, a
 * date preset label) are read off the option lists and `IMPACT_REPORT_DATE_PRESET_LABELS` above,
 * never restated here.
 */

/**
 * The collapsed campaign pill's label once two or more campaigns are selected — mirroring the
 * prototype's `selected.length + ' campaigns'`. This is WHY the bar caps at three physical pills
 * (cause + campaigns + period) regardless of how many campaigns are actually selected: a fourth,
 * fifth, hundredth campaign never grows the bar, it only changes this one number.
 */
export function buildImpactReportCampaignCountPillLabel(count: number): string {
  return `${count} campaigns`;
}

/**
 * Per-pill remove labels, named specifically enough that a screen-reader user with several cause
 * pills open (or a cause pill next to a campaign pill) can tell them apart — "Remove filter" alone
 * would read identically for every pill on the bar.
 */
export function buildImpactReportRemoveCauseFilterLabel(causeName: string): string {
  return `Remove cause filter: ${causeName}`;
}
export function buildImpactReportRemoveCampaignFilterLabel(campaignName: string): string {
  return `Remove campaign filter: ${campaignName}`;
}

/**
 * The collapsed campaign pill's ✕. Unlike the single-campaign pill's remove control, this one
 * always clears the WHOLE campaign selection (there is no single campaign left to name once two or
 * more are collapsed), so its label says so rather than naming one.
 */
export const IMPACT_REPORT_CLEAR_CAMPAIGN_FILTER_LABEL = "Clear campaign filter";

/**
 * The collapsed campaign pill's BODY. Clicking it opens the campaign picker — it removes nothing —
 * so this is a `title`/accessible description, never phrased like the ✕ labels above.
 */
export const IMPACT_REPORT_CAMPAIGN_PILL_BODY_LABEL = "Show selected campaigns";

export const IMPACT_REPORT_REMOVE_DATE_FILTER_LABEL = "Remove date filter";

/**
 * Shown only when `hasActiveFilters` is true. Resets cause, campaign and date together via
 * `clearImpactReportControls()` — never the view mode, which this affordance has no way to touch.
 */
export const IMPACT_REPORT_CLEAR_ALL_FILTERS_LABEL = "Clear all filters";

/**
 * ── The collapsed filters pill ──────────────────────────────────────────────────────────────
 *
 * Past this many active filters, `ImpactFilterPills` collapses them into ONE pill that opens a
 * floating panel, instead of scrolling three-plus individual pills past the four controls' own
 * fixed-width group (the failure `ImpactControlBar.tsx`'s control-group comment documents at
 * 1440px: 3 cause pills + 1 collapsed campaign pill overflowed the space left over, clipping
 * "3 campaigns" mid-pill and dropping clear-all entirely). Below the threshold, pills still render
 * individually — the at-a-glance read of a cause's name or a date range IS a pill's purpose, and
 * collapsing one or two behind an extra click would throw that away for no space actually saved.
 *
 * Chosen as a sensible default, not measured against a hard layout budget: three physical pills
 * (cause + the campaign group + date) is exactly the combination that first overflowed, so
 * collapsing AT three removes that combination outright. Revisit if a design pass wants a
 * different number.
 */
/**
 * The width at or below which the control bar runs in its COMPACT form: the four controls show as
 * icons only, and the pills collapse to a single pill at the first active filter.
 *
 * Estimated by hand from a viewport width: full-width controls were ~120px each, so four of them
 * plus their 12px gaps were 516px; clear-all with its margin is ~98px; a collapsed pill with the
 * outer gap is ~133px — 747px of content, plus the 240px expanded sidebar, is about 987px of
 * VIEWPORT. The controls have since grown, so that sum no longer holds exactly, and no viewport
 * number can track the widths it stands in for.
 *
 * So this breakpoint is no longer what keeps clear-all off the controls. `ImpactFilterPills`
 * measures the room it is actually given against the pills' own measured width plus clear-all's,
 * and when they do not fit, collapses the pills and moves clear-all into the pill's panel at any
 * viewport width. What this still decides is when the controls go icon-only (`ImpactFilterMenu`)
 * and when the pills collapse at the first filter regardless.
 *
 * This is deliberately NOT the app-wide `useIsMobile` breakpoint (767px). That one describes the
 * device; this one describes whether four words fit beside a filter pill, and they are different
 * questions with different answers.
 */
export const IMPACT_REPORT_BAR_COMPACT_MAX_PX = 1023;

/**
 * Once `ImpactFilterPills` has moved clear-all into the collapsed pill's panel, how far PAST the
 * inline form's measured need the pills area must grow before the link comes back beside the
 * pills. A resize hovering at that edge, or sub-pixel drift between the recorded and re-measured
 * widths, then cannot flip the bar back and forth between its two forms.
 *
 * A deliberate default, not a measured number: a small dead band against flicker. A pixel or two
 * would stop the flicker; much more would only keep the collapsed form longer than it is needed.
 */
export const IMPACT_REPORT_INLINE_CLEAR_ALL_HYSTERESIS_PX = 8;

/**
 * Stands in for the inline clear-all link's measured width until it has rendered once. The label
 * at 12.5px bold Lato is ~90px; 110 keeps a safe margin above it so a guess can only err towards
 * collapsing early, never towards rendering a link that does not fit.
 */
export const IMPACT_REPORT_CLEAR_ALL_FALLBACK_WIDTH_PX = 110;

/**
 * The 320px floor tier — WCAG 2.1 SC 1.4.10 (Reflow) requires content to work at 320 CSS px, which
 * is also a 1280px desktop at 400% zoom. Below this the controls narrow from 44px to 40px squares,
 * the gap between them tightens, and the collapsed filter pill drops its word for a bare count.
 */
export const IMPACT_REPORT_BAR_NARROW_MAX_PX = 399;

export const IMPACT_REPORT_PILL_COLLAPSE_THRESHOLD = 3;

/**
 * The collapsed pill's own label — "N filters", never "N pills": a pill is this app's rendering
 * choice, "filters" is what the user thinks they selected. Singular for exactly one, which only
 * happens below the mobile breakpoint, where `ImpactFilterPills` collapses at one active filter
 * rather than waiting for `IMPACT_REPORT_PILL_COLLAPSE_THRESHOLD` — four icon-only controls plus
 * even a single full-width pill is already tight at 375px.
 */
export function buildImpactReportCollapsedFiltersPillLabel(count: number): string {
  return count === 1 ? "1 filter" : `${count} filters`;
}

/**
 * The collapsed pill's accessible name and `title` — names the ACTION (opening the list), not just
 * the count, so it reads sensibly to a screen reader independent of the visible label beside it.
 */
export function buildImpactReportCollapsedFiltersPillAriaLabel(count: number): string {
  return `Show ${count === 1 ? "1 active filter" : `${count} active filters`}`;
}

/** The floating panel's own group label, read by assistive tech on open. */
export const IMPACT_REPORT_ACTIVE_FILTERS_PANEL_ARIA_LABEL = "Active filters";

/**
 * ── The page's error state ──────────────────────────────────────────────────────────────────
 *
 * A failed query is never one of the empty states — telling a client they have no impact for a
 * transport failure answers a question they did not ask. The title is fixed regardless of cause;
 * the body is the API's OWN message when the failed response carried one (read via
 * `components/impactReport/impact-report-error.ts#resolveImpactReportErrorMessage`), falling
 * back to this generic advice only when it did not.
 */
export const IMPACT_REPORT_ERROR_STATE_TITLE = "We couldn't load your impact report";
export const IMPACT_REPORT_ERROR_STATE_FALLBACK_BODY =
  "If this keeps happening, try narrowing the report's filters. Otherwise, try again.";

/**
 * The error state when the reporting service is down or refused Platform
 * (`isImpactReportUnavailableError`). An outage is neither an empty result nor the client's
 * filters, so this copy never mentions filters.
 */
export const IMPACT_REPORT_UNAVAILABLE_STATE_TITLE = "Impact reporting is temporarily unavailable";
export const IMPACT_REPORT_UNAVAILABLE_STATE_BODY =
  "Your impact data is safe. Please try again later.";

/**
 * A deferred report — too large to build while the page waits, so it is emailed when ready. The
 * page keeps polling meanwhile, so the body says both. The generic body is for when the recipient
 * is unknown (the emailed link's own page never learns it).
 */
export const IMPACT_REPORT_DEFERRED_TITLE = "Your report is being prepared";
export function buildImpactReportDeferredBody(recipient: string): string {
  return `It's a large report, so we'll email it to ${recipient} when it's ready. You can also keep this page open. It updates automatically.`;
}
export const IMPACT_REPORT_DEFERRED_GENERIC_BODY =
  "It's a large report, so we'll email it to you when it's ready. You can also keep this page open. It updates automatically.";

/** A deferred report whose job failed. The job's own error text is never shown. */
export const IMPACT_REPORT_JOB_FAILED_TITLE = "We couldn't finish this report";
export const IMPACT_REPORT_JOB_FAILED_BODY =
  "Please try building it again from the Impact Report page.";
/** On `/impact` itself: clears the campaign selection, back to the pre-selection prompt. */
export const IMPACT_REPORT_JOB_FAILED_ACTION_LABEL = "Start over";

/**
 * `/impact` polling a deferred job that 404s — `REPORT_JOB_NOT_FOUND`, meaning Crunchy expired or
 * lost the job before the browser tab ever saw it complete. Distinct from `IMPACT_REPORT_JOB_
 * NOT_FOUND_TITLE`/`_BODY` below, which are the EMAILED link's own dead-link state: this one keeps
 * the reader on `/impact` with a way forward for the SAME selection, so it is worded and actioned
 * differently — "It may have expired" rather than "isn't available", and one button that issues a
 * single fresh request rather than a link back to a page the reader is already on.
 *
 * The job's own error text is never shown, same rule as `IMPACT_REPORT_JOB_FAILED_BODY` above.
 */
export const IMPACT_REPORT_JOB_GONE_TITLE = "This report is no longer available";
export const IMPACT_REPORT_JOB_GONE_BODY = "It may have expired. You can build it again.";
/**
 * Forgets the dead deferral (react-query cache AND its sessionStorage entry, via
 * `useDiscardImpactReport`) for the SAME selection, THEN explicitly refetches the report query —
 * removing a query does not, by itself, re-fetch it, so both steps are needed; see
 * `pages/ImpactReport.tsx#ImpactReportJobGoneState`'s `onBuildAgain` for why. Together: exactly one
 * new `GET /reports/client-impact-report`, and only on this click.
 */
export const IMPACT_REPORT_JOB_GONE_ACTION_LABEL = "Build it again";

/** The emailed report link's page: every non-report state links back to `/impact`. */
export const IMPACT_REPORT_JOB_BACK_LINK_LABEL = "Go to Impact Report";
/** A finished report with no causes. The page has no controls, so no selection empty state. */
export const IMPACT_REPORT_JOB_EMPTY_BODY = "There's no impact to show in this report.";

/** An emailed report link that is expired, unknown or someone else's — the API cannot tell which. */
export const IMPACT_REPORT_JOB_NOT_FOUND_TITLE = "This report link isn't available";
export const IMPACT_REPORT_JOB_NOT_FOUND_BODY =
  "It may have expired (links last 7 days) or it belongs to someone else.";

/** The app-bar title on the emailed report link's page. */
export const IMPACT_REPORT_JOB_APP_BAR_TITLE = "Impact Report";
